import { revalidateResolvedFrameworkPack } from '../../supabase/functions/_shared/generationAuthority';
import { resolveCurrentFrameworkPack, resolveLeaderFrameworkPack } from '../../supabase/functions/_shared/resolveLeaderFrameworkPack';
import type { DbFrameworkPack } from '../../supabase/functions/_shared/frameworkPackDb';
import { loadPracticeContext, type PracticeContext } from '../../supabase/functions/_shared/practiceSelectionContext';
import { edgeColumns } from "../../supabase/functions/_shared/edgeColumns";
import { MEMORY_ARCHIVE_LIKE, isMemoryArchiveType } from "@/lib/leaderMemoryVisibility";
import { withholdsUnboundBiInterpretations, revalidateGovernedBiContext, loadGovernedBiContext, projectBiGovernedGraph, type BiRpcClient } from "@/lib/governedBiRetrieval";
import { useEffect, useRef } from "react";
import { createContextAssemblyReceiptEmitter, type ContextSelectionManifest, type ContextSourceReceipt } from "../../supabase/functions/_shared/contextAssemblyReceipt";
import { observeContextQueries } from "../../supabase/functions/_shared/contextQueryObserver";
import { useQuery } from "@tanstack/react-query";
import { supabase as supabaseClient } from "@/integrations/supabase/client";
import {
  coachVisibleLeaderMemoryVisibilityList,
  isCoachVisibleLeaderMemoryVisibility,
} from "@/lib/leaderMemoryVisibility";
import { buildCoachSessionPrepContinuityViewModel, type CoachSessionPrepContinuityViewModel } from "@/lib/behavior/viewModels/buildCoachSessionPrepContinuity";
import {
  buildCoachSessionPrepBehavioralObservation,
  type CoachSessionPrepCanonicalBehaviorData,
} from "@/lib/behavior/viewModels/buildCoachSessionPrepBehavioralObservation";
import type { InterventionMemory } from "@/lib/behavior/interventionMemory";
import { parseLatestBehavioralPatterns } from "@/lib/behavioralPatternLedger";
import { appQueryKeys } from "@/lib/queryKeys";
import { emitSchemaFallbackTelemetry } from "@/lib/schemaFallbackTelemetry";
import {
  buildCoachLeaderDetailSourceReadiness,
  type CoachLeaderDetailOptionalSourceState,
  type CoachLeaderDetailSourceReadiness,
} from "@/lib/coachLeaderDetail/buildCoachLeaderDetailSourceReadiness";
import {
  canonicalizeCoachLeaderLongitudinalSourceType,
  type CoachLeaderAggregateEvidenceRef,
  type CoachLeaderAuthorizedSourceRef,
} from "@/lib/coachLeaderDetail/buildCoachLeaderLongitudinalRead";
import {
  buildCoachVeeraBehavioralReadModel,
  type CoachVeeraBehavioralReadModel,
} from "@/lib/behavior/coachVeeraBehavioralReadModel";
import { normalizeBehavioralKey } from "@/lib/behavior/substrate";
import type { GraphSnapshot } from "../../supabase/functions/_shared/contextGraph";
import type {
  ObservationV2,
  BehavioralThemeV2,
  ObservationClusterV2,
} from "../../supabase/functions/_shared/bi-v2/contracts";

export type ReflectionItem = {
  id: string;
  created_at: string | null;
  text: string | null;
  title: string;
  source: "post_reflection" | "shared_reflection" | "assessment_reflection";
  link: string | null;
  meeting_id: string | null;
  leader_id?: string;
  canonical_source_id?: string;
};

export type TranscriptItem = {
  id: string;
  created_at: string;
  title: string;
  meeting_start_time: string | null;
  summary: string | null;
  source: "transcript";
  link: string;
  meeting_id: string | null;
  analysis_status: string | null;
  coach_verified: boolean | null;
  leader_id?: string;
};

export type ArtifactItem = {
  id: string;
  created_at: string;
  title: string;
  type: string;
  source: "artifact";
  link: string | null;
  leader_id?: string;
};

export type PracticeSummaryItem = {
  id: string;
  meeting_id: string;
  meeting_title: string | null;
  meeting_start_time: string;
  transcript_upload_id: string | null;
  reflection_rating: number | null;
  leader_reflection_text: string | null;
  transcript_summary: string | null;
  behavior_insights: string | null;
  coaching_prompt: string | null;
  created_at: string;
};

export type PracticeFocusEvidenceItem = {
  id: string;
  suggestion_id: string;
  leader_id: string;
  source_type: string;
  source_id: string;
  source_key: string | null;
  graph_node_id: string | null;
  evidence_role: string;
  evidence_visibility_scope: string;
  leader_safe_summary: string | null;
  observed_at: string | null;
  created_at: string | null;
};

export type PracticeFocusSuggestionItem = {
  id: string;
  leader_id: string;
  coach_id: string | null;
  focus_key: string;
  title: string;
  practice_prompt: string;
  rationale_summary: string | null;
  status: string;
  visibility_scope: string;
  ledger_visibility_scope: string;
  confidence_level: string | null;
  priority_score: number | null;
  primary_evidence_count: number | null;
  due_at: string | null;
  approved_for_leader_at: string | null;
  leader_response: string | null;
  leader_response_version: number | null;
  responded_at: string | null;
  last_observed_at: string | null;
  created_at: string | null;
  evidence: PracticeFocusEvidenceItem[];
};

export type MeetingFeedbackItem = {
  id: string;
  meeting_id: string;
  attendee_count: number | null;
  meeting_type: string | null;
  was_good_fit: boolean;
  feedback_reason: string | null;
  created_at: string;
};

export type LeaderLedgerEventItem = {
  id: string;
  created_at: string;
  event_type: string;
  source_type: string;
  actor_role: string | null;
  confidence_level: string | null;
  metadata: Record<string, unknown> | null;
};

export type CoachVisibleLeaderMemoryInputItem = {
  id: string;
  source_type: string | null;
  memory_label: string | null;
  title: string | null;
  approved_summary: unknown;
  visibility: "share_summary_with_coach" | "share_full_source_with_coach";
  updated_at: string | null;
};

export type ContextGraphEdgeItem = {
  id: string;
  leader_id: string;
  from_node_id: string;
  to_node_id: string;
  edge_type: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

export type CoachLensContext = {
  coaching_philosophy: string | null;
  frameworks: string | null;
  voice_summary: string | null;
};

export type CoachSessionPrepAgreementConversationRecord = {
  id: string;
  source: string;
  created_at: string;
  summary: string;
  transcriptUploadId?: string | null;
  tags?: string[];
};

export type CoachSessionPrepAgreementRecord = {
  namedAgreements: string[];
  conversationRecords: CoachSessionPrepAgreementConversationRecord[];
};

export type CoachSessionPrepLatestGrowthRecord = {
  source: string;
  created_at: string;
  workedOn: string[];
  direction: string[];
  nextConversationSignals: string[];
  transcriptUploadId: string | null;
} | null;

export type CoachSessionPrepBehavioralObservation = {
  source: string | null;
  created_at: string | null;
  moment: string;
  pattern: string | null;
  reflection: string | null;
  isFallback: boolean;
};

export type CoachSessionPrepLongitudinalIntelligence = Readonly<{
  aggregate: CoachVeeraBehavioralReadModel;
  aggregateEvidenceRefs: readonly CoachLeaderAggregateEvidenceRef[];
  aggregateReadiness: Readonly<{
    canonical: "ready" | "empty" | "retryable_error";
    graph: "ready" | "empty" | "retryable_error";
    compatibility: "ready" | "empty" | "retryable_error";
    crosswalk: "ready" | "empty" | "retryable_error";
    legacy: "ready" | "empty" | "retryable_error";
  }>;
  authorizedSources:
    | Readonly<{ status: "ready" | "partial"; sources: readonly CoachLeaderAuthorizedSourceRef[] }>
    | Readonly<{ status: "operational_error"; sources: readonly [] }>;
  legacyEvidencePresent: boolean | null;
  observationWindowTruncated: boolean | null;
}>;

export type CoachSessionPrepData = {
  /** Executed query outcomes only; helper reads outside this fetch remain unattributed. */
  contextSources?: ContextSourceReceipt[];
  frameworkPack?: DbFrameworkPack|null;
  frameworkContextStatus?: 'ready'|'unavailable';
  practiceContext?: PracticeContext;
  profile: {
    id: string;
    full_name: string | null;
    email: string | null;
    coach_id: string | null;
  };
  onboarding: {
    best_self_narrative?: string | null;
    meaning_anchor?: string | null;
    trigger_moments?: string | string[] | null;
    leadership_lens?: string | null;
    created_at?: string | null;
  } | null;
  transcripts: TranscriptItem[];
  reflections: ReflectionItem[];
  artifacts: ArtifactItem[];
  practiceSummaries: PracticeSummaryItem[];
  practiceFocusSuggestions: PracticeFocusSuggestionItem[];
  meetingFeedback: MeetingFeedbackItem[];
  ledgerEvents: LeaderLedgerEventItem[];
  leaderMemoryInputs: CoachVisibleLeaderMemoryInputItem[];
  coachLens: CoachLensContext;
  canonicalBehavior?: CoachSessionPrepCanonicalBehaviorData;
  agreementRecord: CoachSessionPrepAgreementRecord;
  continuity: CoachSessionPrepContinuityViewModel;
  latestCoachGrowthRecord: CoachSessionPrepLatestGrowthRecord;
  behavioralObservation: CoachSessionPrepBehavioralObservation;
  longitudinalIntelligence: CoachSessionPrepLongitudinalIntelligence;
  interventionMemory: InterventionMemory;
  sourceReadiness: CoachLeaderDetailSourceReadiness;
};

type CoachPracticeFocusQueryResult<T> = Promise<{ data: T[] | null; error: unknown }>;

interface CoachPracticeFocusQueryBuilder<T> {
  select(columns: string): CoachPracticeFocusFilterBuilder<T>;
}

interface CoachPracticeFocusFilterBuilder<T> {
  eq(column: string, value: string): CoachPracticeFocusFilterBuilder<T>;
  in(column: string, values: string[]): CoachPracticeFocusFilterBuilder<T>;
  order(column: string, options?: { ascending?: boolean; nullsFirst?: boolean }): {
    limit(count: number): CoachPracticeFocusQueryResult<T>;
  };
}

interface CoachPracticeFocusDbClient {
  from(table: "practice_focus_suggestions"): CoachPracticeFocusQueryBuilder<Omit<PracticeFocusSuggestionItem, "evidence">>;
  from(table: "coach_practice_focus_suggestion_evidence"): CoachPracticeFocusQueryBuilder<PracticeFocusEvidenceItem>;
}

function countByNodeType(nodes: Array<{ node_type: string }>): Record<string, number> {
  return nodes.reduce<Record<string, number>>((acc, node) => {
    acc[node.node_type] = (acc[node.node_type] || 0) + 1;
    return acc;
  }, {});
}

function buildCoachEvidenceLink(leaderId: string, sourceType: string, sourceId: string): string | null {
  const base = `/coach/leaders/${leaderId}`;
  switch (sourceType) {
    case "meeting":
      return `${base}?section=evidence&meetingId=${sourceId}`;
    case "transcript_upload":
    case "transcript":
      return `/coach/post-meeting/${sourceId}`;
    case "post_meeting_reflection":
      return `${base}?section=evidence&type=reflection&id=${sourceId}`;
    case "shared_reflection":
      return `${base}?section=evidence&type=shared-reflection&id=${sourceId}`;
    case "coach_note":
      return `${base}?section=notes&id=${sourceId}`;
    case "leader_artifact":
      return `${base}?section=evidence&type=artifact&id=${sourceId}`;
    default:
      return `${base}?section=evidence`;
  }
}

function isMissingSchemaObject(error: unknown) {
  const err = error as { code?: string; message?: string; details?: string; hint?: string } | null;
  if (!err) return false;
  if (err.code === "42P01" || err.code === "42703" || err.code === "PGRST204" || err.code === "PGRST205") return true;
  const message = `${err.message || ""} ${err.details || ""} ${err.hint || ""}`.toLowerCase();
  return (
    message.includes("does not exist")
    || message.includes("could not find the table")
    || message.includes("could not find the column")
    || message.includes("could not find a relationship")
    || message.includes("schema cache")
  );
}

function optionalSourceState(
  responses: Array<{ data: unknown; error: unknown }>,
): CoachLeaderDetailOptionalSourceState {
  if (responses.some((response) => Boolean(response.error))) return "retryable_error";
  const hasData = responses.some((response) => (
    Array.isArray(response.data) ? response.data.length > 0 : Boolean(response.data)
  ));
  return hasData ? "ready" : "empty";
}

type MeetingBehaviorObservationRow = {
  meeting_id: string | null;
  behavior_id: string | null;
  polarity: "supporting" | "opposing" | null;
  confidence: "low" | "medium" | "high" | null;
  context_tags: string[] | null;
  evidence_excerpt: string | null;
  observed_at: string | null;
  transcript_upload_id: string | null;
  source_type: string | null;
  analysis_version: number | null;
  analysis_item_kind: string | null;
  analysis_item_index: number | null;
  analysis_item_digest: string | null;
  safe_summary_field: string | null;
  quote_occurrence_key: string | null;
  grounding_policy_version: string | null;
};

type QueryResponse = Readonly<{ data: unknown; error: unknown }>;

type AggregateReadinessInput = Readonly<{
  themes: QueryResponse;
  clusters: QueryResponse;
  graphNodes: QueryResponse;
  graphEdges: QueryResponse;
  compatibility: QueryResponse;
  compatibilitySummary: unknown;
  crosswalk: QueryResponse;
  legacy: QueryResponse;
}>;

const nonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

function responseHasRows(response: QueryResponse): boolean {
  return Array.isArray(response.data) && response.data.length > 0;
}

function deriveAggregateReadiness(
  input: AggregateReadinessInput,
): CoachSessionPrepLongitudinalIntelligence["aggregateReadiness"] {
  const canonical = input.themes.error || input.clusters.error
    ? "retryable_error"
    : responseHasRows(input.themes) || responseHasRows(input.clusters)
      ? "ready"
      : "empty";
  const graph = input.graphNodes.error || input.graphEdges.error
    ? "retryable_error"
    : responseHasRows(input.graphNodes) || responseHasRows(input.graphEdges)
      ? "ready"
      : "empty";
  const compatibility = input.compatibility.error
    ? "retryable_error"
    : input.compatibilitySummary
      ? "ready"
      : "empty";
  const crosswalk = input.crosswalk.error
    ? "retryable_error"
    : responseHasRows(input.crosswalk)
      ? "ready"
      : "empty";
  const legacy = input.legacy.error
    ? "retryable_error"
    : responseHasRows(input.legacy)
      ? "ready"
      : "empty";
  return { canonical, graph, compatibility, crosswalk, legacy };
}

function hasCompleteLineageBundle(row: MeetingBehaviorObservationRow): boolean {
  return Boolean(
    nonEmptyString(row.meeting_id)
    && nonEmptyString(row.transcript_upload_id)
    && row.source_type === "transcript"
    && nonEmptyString(row.behavior_id)
    && (row.polarity === "supporting" || row.polarity === "opposing")
    && (row.confidence === "low" || row.confidence === "medium" || row.confidence === "high")
    && nonEmptyString(row.evidence_excerpt)
    && nonEmptyString(row.observed_at)
    && /^\d{4}-\d{2}-\d{2}T/.test(row.observed_at)
    && Number.isInteger(row.analysis_version) && Number(row.analysis_version) > 0
    && nonEmptyString(row.analysis_item_kind)
    && Number.isInteger(row.analysis_item_index) && Number(row.analysis_item_index) >= 0
    && nonEmptyString(row.analysis_item_digest)
    && nonEmptyString(row.safe_summary_field)
    && nonEmptyString(row.quote_occurrence_key)
    && nonEmptyString(row.grounding_policy_version)
  );
}

function projectAuthorizedLongitudinalSources(params: {
  transcriptResponse: QueryResponse;
  reflectionResponse: QueryResponse;
  artifactResponse: QueryResponse;
  transcripts: readonly TranscriptItem[];
  reflections: readonly ReflectionItem[];
  artifacts: readonly ArtifactItem[];
}): CoachSessionPrepLongitudinalIntelligence["authorizedSources"] {
  const optionalSourceErrors = [params.reflectionResponse.error, params.artifactResponse.error].filter(Boolean);
  if (
    params.transcriptResponse.error
    || optionalSourceErrors.some((error) => !isMissingSchemaObject(error))
  ) {
    return { status: "operational_error", sources: [] };
  }

  const sources: CoachLeaderAuthorizedSourceRef[] = [
    ...params.transcripts.map((transcript) => ({
      sourceType: "transcript_upload",
      sourceId: transcript.id,
      meetingId: transcript.meeting_id,
      title: transcript.title,
      occurredAt: transcript.meeting_start_time ?? transcript.created_at,
      route: transcript.link,
    })),
    ...(params.reflectionResponse.error ? [] : params.reflections
      .filter((reflection) => reflection.source === "post_reflection" && Boolean(reflection.link))
      .map((reflection) => ({
        sourceType: "post_meeting_reflection",
        sourceId: reflection.canonical_source_id ?? reflection.id,
        meetingId: reflection.meeting_id,
        title: reflection.title,
        occurredAt: reflection.created_at,
        route: reflection.link!,
      }))),
    ...(params.artifactResponse.error ? [] : params.artifacts
      .filter((artifact) => Boolean(artifact.link))
      .map((artifact) => ({
        sourceType: "leader_artifact",
        sourceId: artifact.id,
        meetingId: null,
        title: artifact.title,
        occurredAt: artifact.created_at,
        route: artifact.link!,
      }))),
  ];
  const status = optionalSourceErrors.length > 0 ? "partial" : "ready";
  return { status, sources };
}

function logError(code: "prep_ledger_read_failed" | "prep_graph_nodes_read_failed" | "prep_graph_edges_read_failed"): void {
  try { console.warn(code); } catch { /* Diagnostics do not change retrieval readiness. */ }
}

export async function fetchCoachSessionPrepData(params: {
  coachId: string;
  leaderId: string;
}): Promise<CoachSessionPrepData> {
  const { coachId, leaderId } = params;
  const contextSources: ContextSourceReceipt[] = [];
  const supabase = observeContextQueries(supabaseClient, (source) => contextSources.push(source)) as typeof supabaseClient;

  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .select("id, full_name, email, coach_id")
    .eq("id", leaderId)
    .maybeSingle();

  if (profileError) throw profileError;
  if (!profile) throw new Error("Leader not found or not assigned to you");

  const { data: link, error: linkError } = await supabase
    .from("coach_leader_links")
    .select("leader_id")
    .eq("coach_id", coachId)
    .eq("leader_id", leaderId)
    .maybeSingle();

  if (linkError) throw linkError;
  if (!link && profile.coach_id !== coachId) {
    throw new Error("Leader not found or not assigned to you");
  }

  const biContext = await loadGovernedBiContext(supabase as unknown as BiRpcClient, leaderId, "coach");
  contextSources.push({kind:"governed_bi",status:biContext.status,row_count:biContext.themes.length,reason_code:biContext.reason});
  const biRetrievalError=["query_failed","invalid_payload"].includes(biContext.reason) ? {code:biContext.reason} : null;
  const [
    onboarding,
    transcriptRows,
    postReflections,
    assessments,
    sharedReflections,
    artifacts,
    practiceSummaries,
    practiceFocusSuggestions,
    meetingFeedback,
    ledgerEvents,
    contextGraphNodes,
    contextGraphEdges,
    behavioralObservations,
    meetingBehaviorObservations,
    behavioralThemes,
    behavioralClusters,
    coachProfile,
    activeCoachTwin,
    coachAgreementMemory,
    leaderMemoryInputs,
  ] = await Promise.all([
    supabase
      .from("leader_onboarding")
      .select("best_self_narrative, meaning_anchor, trigger_moments, leadership_lens, created_at")
      .eq("leader_id", leaderId)
      .maybeSingle(),
    supabase
      .from("transcript_uploads")
      .select("id, created_at, summary_text, meeting_id, analysis_status, coach_verified, meetings!transcript_uploads_meeting_id_fkey(title, start_time)")
      .eq("leader_id", leaderId)
      .order("created_at", { ascending: false }),
    supabase
      .from("post_meeting_reflections")
      .select("id, created_at, reflection_text, transcript_upload_id, meeting_id")
      .eq("leader_id", leaderId)
      .order("created_at", { ascending: false }),
    supabase
      .from("assessments")
      .select("id, completed_at, success_moment, presence_rating, was_hard, meeting_id, leader_behaviors!inner(leader_id)")
      .eq("leader_behaviors.leader_id", leaderId)
      .order("completed_at", { ascending: false }),
    supabase
      .from("shared_reflections")
      .select("id, created_at, content")
      .eq("leader_id", leaderId)
      .order("created_at", { ascending: false }),
    supabase
      .from("leader_artifacts")
      .select("id, created_at, artifact_type, metadata")
      .eq("leader_id", leaderId)
      .order("created_at", { ascending: false }),
    supabase
      .from("practice_summaries")
      .select("id, meeting_id, meeting_title, meeting_start_time, transcript_upload_id, reflection_rating, leader_reflection_text, transcript_summary, behavior_insights, coaching_prompt, created_at")
      .eq("leader_id", leaderId)
      .order("meeting_start_time", { ascending: false }),
    (supabase as unknown as CoachPracticeFocusDbClient)
      .from("practice_focus_suggestions")
      .select("id, leader_id, coach_id, focus_key, title, practice_prompt, rationale_summary, status, visibility_scope, ledger_visibility_scope, confidence_level, priority_score, primary_evidence_count, due_at, approved_for_leader_at, leader_response, leader_response_version, responded_at, last_observed_at, created_at")
      .eq("leader_id", leaderId)
      .eq("coach_id", coachId)
      .eq("visibility_scope", "coach_reviewed")
      .eq("ledger_visibility_scope", "coach_only")
      .in("status", ["suggested", "accepted", "practiced", "partially_practiced"])
      .order("last_observed_at", { ascending: false, nullsFirst: false })
      .limit(5),
    supabase
      .from("meeting_feedback")
      .select("id, meeting_id, attendee_count, meeting_type, was_good_fit, feedback_reason, created_at")
      .eq("leader_id", leaderId)
      .order("created_at", { ascending: false }),
    supabase
      .from("leader_memory_ledger")
      .select("id, created_at, event_type, source_type, actor_role, confidence_level, metadata")
      .not("source_type", "like", MEMORY_ARCHIVE_LIKE)
      .eq("leader_id", leaderId)
      .order("created_at", { ascending: false })
      .limit(300),
    supabase
      .from("context_graph_nodes")
      .select("id, node_type, source_type, source_id, title, created_at")
      .not("source_type", "like", MEMORY_ARCHIVE_LIKE)
      .not("node_type", "like", MEMORY_ARCHIVE_LIKE)
      .eq("leader_id", leaderId)
      .order("created_at", { ascending: false })
      .limit(30),
    supabase
      .from("context_graph_edges")
      .select(edgeColumns("context_graph_edges", ["id", "leader_id", "from_node_id", "to_node_id", "edge_type", "metadata", "created_at"], [
        { alias: "from_node", table: "context_graph_nodes", foreignKey: "context_graph_edges_from_node_id_fkey", columns: ["node_type", "source_type"] },
        { alias: "to_node", table: "context_graph_nodes", foreignKey: "context_graph_edges_to_node_id_fkey", columns: ["node_type", "source_type"] },
      ]))
      .not("from_node.node_type", "like", MEMORY_ARCHIVE_LIKE)
      .not("from_node.source_type", "like", MEMORY_ARCHIVE_LIKE)
      .not("to_node.node_type", "like", MEMORY_ARCHIVE_LIKE)
      .not("to_node.source_type", "like", MEMORY_ARCHIVE_LIKE)
      .eq("leader_id", leaderId)
      .order("created_at", { ascending: false })
      .limit(60),
    Promise.resolve({data:biContext.observations,error:biRetrievalError}),
    supabase
      .from("meeting_behavior_observations")
      .select("meeting_id, behavior_id, polarity, confidence, context_tags, evidence_excerpt, observed_at, transcript_upload_id, source_type, analysis_version, analysis_item_kind, analysis_item_index, analysis_item_digest, safe_summary_field, quote_occurrence_key, grounding_policy_version")
      .eq("leader_id", leaderId)
      .order("observed_at", { ascending: false })
      .limit(51),
    Promise.resolve({data:biContext.themes,error:biRetrievalError}),
    Promise.resolve({data:biContext.clusters,error:biRetrievalError}),
    supabase
      .from("coach_profiles")
      .select("coaching_philosophy, frameworks")
      .eq("coach_id", coachId)
      .maybeSingle(),
    supabase
      .from("coach_twin_profiles")
      .select("twin_json, status, version")
      .eq("coach_id", coachId)
      .eq("status", "active")
      .order("version", { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from("coach_leader_session_records")
      .select("id, created_at, source_type, transcript_upload_id, worked_on, direction, next_conversation_signals, coach_note, coach_review_v1, leader_plan_draft_v1, published_to_leader, ambiguity_flag, ambiguity_reasons")
      .eq("coach_id", coachId)
      .eq("leader_id", leaderId)
      .order("created_at", { ascending: false })
      .limit(20),
    supabase
      .from("leader_memory_inputs")
      .select("id, source_type, memory_label, title, approved_summary, visibility, updated_at")
      .eq("leader_id", leaderId)
      .eq("status", "approved")
      .in("visibility", coachVisibleLeaderMemoryVisibilityList())
      .order("updated_at", { ascending: false })
      .limit(8),
  ]);

  if (ledgerEvents.error) logError("prep_ledger_read_failed");
  if (contextGraphNodes.error) logError("prep_graph_nodes_read_failed");
  if (contextGraphEdges.error) logError("prep_graph_edges_read_failed");
  if (sharedReflections.error && isMissingSchemaObject(sharedReflections.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.shared_reflections",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (sharedReflections.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_shared_reflections_section",
    });
  }
  if (artifacts.error && isMissingSchemaObject(artifacts.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.leader_artifacts",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (artifacts.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_artifacts_section",
    });
  }
  if (practiceSummaries.error && isMissingSchemaObject(practiceSummaries.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.practice_summaries",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (practiceSummaries.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_practice_summaries_section",
    });
  }
  if (practiceFocusSuggestions.error && isMissingSchemaObject(practiceFocusSuggestions.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.practice_focus_suggestions",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (practiceFocusSuggestions.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_practice_focus_session_prep",
    });
  }
  if (meetingFeedback.error && isMissingSchemaObject(meetingFeedback.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.meeting_feedback",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (meetingFeedback.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_meeting_feedback_section",
    });
  }
  if (contextGraphNodes.error && isMissingSchemaObject(contextGraphNodes.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.context_graph_nodes",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (contextGraphNodes.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_canonical_behavior_graph_context",
    });
  }
  if (contextGraphEdges.error && isMissingSchemaObject(contextGraphEdges.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.context_graph_edges",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (contextGraphEdges.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_canonical_behavior_graph_relationships",
    });
  }
  if (behavioralObservations.error && isMissingSchemaObject(behavioralObservations.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.behavioral_intelligence_v2_observations",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (behavioralObservations.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_canonical_behavior_observations",
    });
  }
  if (behavioralThemes.error && isMissingSchemaObject(behavioralThemes.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.behavioral_intelligence_v2_themes",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (behavioralThemes.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_canonical_behavior_themes",
    });
  }
  if (behavioralClusters.error && isMissingSchemaObject(behavioralClusters.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.behavioral_intelligence_v2_clusters",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (behavioralClusters.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_canonical_behavior_clusters",
    });
  }
  if (coachProfile.error && isMissingSchemaObject(coachProfile.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.coach_profiles",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (coachProfile.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_coach_lens_context",
    });
  }
  if (activeCoachTwin.error && isMissingSchemaObject(activeCoachTwin.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.coach_twin_profiles",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (activeCoachTwin.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_coach_voice_context",
    });
  }
  if (coachAgreementMemory.error && isMissingSchemaObject(coachAgreementMemory.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.coach_leader_session_records",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (coachAgreementMemory.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_agreement_record_section",
    });
  }
  if (leaderMemoryInputs.error && isMissingSchemaObject(leaderMemoryInputs.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.leader_memory_inputs",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (leaderMemoryInputs.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_coach_visible_leader_memory_inputs",
    });
  }

  const transcripts: TranscriptItem[] = (transcriptRows.data || []).map((item) => ({
    id: item.id,
    created_at: item.created_at,
    title: (item.meetings as { title?: string | null } | null)?.title || "Transcript",
    meeting_start_time: (item.meetings as { start_time?: string | null } | null)?.start_time ?? null,
    summary: item.summary_text || null,
    source: "transcript",
    link: `/coach/post-meeting/${item.id}`,
    meeting_id: item.meeting_id,
    analysis_status: item.analysis_status,
    coach_verified: item.coach_verified ?? null,
    leader_id: leaderId,
  }));

  const reflections: ReflectionItem[] = [
    ...((assessments.data || []) as Array<{
      id: string;
      completed_at: string | null;
      success_moment: string | null;
      presence_rating: number;
      was_hard: boolean | null;
      meeting_id: string;
    }>).map((item) => ({
      id: `assessment-${item.id}`,
      created_at: item.completed_at,
      text: item.success_moment
        ? item.success_moment
        : `Presence rating: ${item.presence_rating}/5${item.was_hard ? " (hard)" : ""}.`,
      title: "Practice reflection",
      source: "assessment_reflection" as const,
      link: `/coach/leaders/${leaderId}?section=evidence&type=assessment&id=${item.id}&meetingId=${item.meeting_id}`,
      meeting_id: item.meeting_id,
      leader_id: leaderId,
      canonical_source_id: item.id,
    })),
    ...(postReflections.data || []).map((item) => ({
      id: item.id,
      created_at: item.created_at,
      text: item.reflection_text,
      title: "Post-meeting reflection",
      source: "post_reflection" as const,
      link: `/coach/leaders/${leaderId}?section=evidence&type=reflection&id=${item.id}${item.meeting_id ? `&meetingId=${item.meeting_id}` : ""}`,
      meeting_id: item.meeting_id,
      leader_id: leaderId,
      canonical_source_id: item.id,
    })),
    ...((sharedReflections.data || []) as Array<{ id: string; created_at: string; content: string | null }>).map((item) => ({
      id: item.id,
      created_at: item.created_at,
      text: item.content,
      title: "Shared reflection",
      source: "shared_reflection" as const,
      link: `/coach/leaders/${leaderId}?section=evidence&type=shared-reflection`,
      meeting_id: null,
      leader_id: leaderId,
      canonical_source_id: item.id,
    })),
  ];

  const safePracticeFocusSuggestions = practiceFocusSuggestions.error && isMissingSchemaObject(practiceFocusSuggestions.error)
    ? []
    : (practiceFocusSuggestions.data || []);
  const scopedPracticeFocusSuggestions = safePracticeFocusSuggestions as Array<Omit<PracticeFocusSuggestionItem, "evidence">>;
  const practiceFocusSuggestionIds = scopedPracticeFocusSuggestions.map((item) => item.id);
  const practiceFocusEvidence = practiceFocusSuggestionIds.length > 0
    ? await (supabase as unknown as CoachPracticeFocusDbClient)
      .from("coach_practice_focus_suggestion_evidence")
      .select("id, suggestion_id, leader_id, source_type, source_id, source_key, graph_node_id, evidence_role, evidence_visibility_scope, leader_safe_summary, observed_at, created_at")
      .eq("leader_id", leaderId)
      .in("suggestion_id", practiceFocusSuggestionIds)
      .order("observed_at", { ascending: false, nullsFirst: false })
      .limit(50)
    : { data: [], error: null };

  if (practiceFocusEvidence.error && isMissingSchemaObject(practiceFocusEvidence.error)) {
    emitSchemaFallbackTelemetry({
      objectName: "public.coach_practice_focus_suggestion_evidence",
      flow: "coach_leader_session_prep_load",
      actorRole: "coach",
      route: `/coach/leaders/${leaderId}`,
      errorCode: (practiceFocusEvidence.error as { code?: string } | null)?.code || null,
      degradedMode: "omit_practice_focus_evidence_session_prep",
    });
  }
  const safeArtifacts = artifacts.error ? [] : (artifacts.data || []);
  const safePracticeSummaries = practiceSummaries.error ? [] : (practiceSummaries.data || []);
  const safePracticeFocusEvidence = practiceFocusEvidence.error ? [] : (practiceFocusEvidence.data || []);
  const safeMeetingFeedback = meetingFeedback.error ? [] : (meetingFeedback.data || []);
  const safeContextGraphNodes = contextGraphNodes.error ? [] : (contextGraphNodes.data || []);
  const safeContextGraphEdges = contextGraphEdges.error ? [] : ((contextGraphEdges.data || []) as unknown as ContextGraphEdgeItem[]).map(edge => ({
    id: edge.id, leader_id: edge.leader_id, from_node_id: edge.from_node_id, to_node_id: edge.to_node_id,
    edge_type: edge.edge_type, metadata: edge.metadata, created_at: edge.created_at,
  }));
  const safeBehavioralObservations = behavioralObservations.error ? [] : (behavioralObservations.data || []);
  const safeBehavioralThemes = behavioralThemes.error ? [] : (behavioralThemes.data || []);
  const safeBehavioralClusters = behavioralClusters.error ? [] : (behavioralClusters.data || []);
  const safeLeaderMemoryInputs = leaderMemoryInputs.error
    ? []
    : ((leaderMemoryInputs.data || []) as CoachVisibleLeaderMemoryInputItem[]).filter((input) => (
      isCoachVisibleLeaderMemoryVisibility(input.visibility)
    ));
  const artifactsItems: ArtifactItem[] = (safeArtifacts as Array<{ id: string; created_at: string; artifact_type: string; metadata?: { file_name?: string | null } }>).map((item) => ({
    id: item.id,
    created_at: item.created_at,
    title: item.metadata?.file_name || "Artifact",
    type: item.artifact_type,
    source: "artifact",
    link: `/coach/leaders/${leaderId}?section=evidence&type=artifact&id=${item.id}`,
    leader_id: leaderId,
  }));

  const practiceFocusEvidenceBySuggestion = new Map<string, PracticeFocusEvidenceItem[]>();
  (safePracticeFocusEvidence as PracticeFocusEvidenceItem[]).forEach((item) => {
    if (!practiceFocusEvidenceBySuggestion.has(item.suggestion_id)) {
      practiceFocusEvidenceBySuggestion.set(item.suggestion_id, []);
    }
    practiceFocusEvidenceBySuggestion.get(item.suggestion_id)?.push(item);
  });
  const practiceFocusSuggestionItems: PracticeFocusSuggestionItem[] = (practiceFocusEvidence.error && isMissingSchemaObject(practiceFocusEvidence.error)
    ? []
    : scopedPracticeFocusSuggestions).map((item) => ({
      ...item,
      evidence: practiceFocusEvidenceBySuggestion.get(item.id) || [],
    }));

  const agreementRows = ((coachAgreementMemory.error && isMissingSchemaObject(coachAgreementMemory.error))
    ? []
    : (coachAgreementMemory.data || [])) as Array<{
      id: string;
      leader_id: string | null;
      created_at: string;
      source_type: string;
      transcript_upload_id: string | null;
      worked_on: string[] | null;
      direction: string[] | null;
      next_conversation_signals: string[] | null;
      ambiguity_reasons: string[] | null;
      ambiguity_flag: boolean | null;
      coach_review_v1: unknown;
      leader_plan_draft_v1: unknown;
      published_to_leader: boolean | null;
      coach_note: string | null;
    }>;

  const prepContinuity = buildCoachSessionPrepContinuityViewModel({
    rows: agreementRows,
  });
  const normalizedLedgerEvents = ((ledgerEvents.data || []) as LeaderLedgerEventItem[])
    .filter(event => !isMemoryArchiveType(event.source_type));
  const compatibilitySummary = null;
  const contextGraph: GraphSnapshot = {
    windowLabel: "recent prep context",
    nodeCounts: countByNodeType(safeContextGraphNodes as Array<{ node_type: string }>),
    recentNodes: (safeContextGraphNodes as Array<{
      id: string;
      node_type: string;
      source_type: string;
      source_id: string;
      title: string | null;
      created_at: string;
    }>).map((node) => ({
      ...node,
      coach_link: buildCoachEvidenceLink(leaderId, node.source_type, node.source_id),
    })),
  };
  const canonicalBehavior: CoachSessionPrepCanonicalBehaviorData = {
    biThemes: safeBehavioralThemes as BehavioralThemeV2[],
    biObservations: safeBehavioralObservations as ObservationV2[],
    biClusters: safeBehavioralClusters as ObservationClusterV2[],
    governedBi:biContext,
    contextGraph:projectBiGovernedGraph(contextGraph,biContext) ?? contextGraph,
    contextGraphEdges: safeContextGraphEdges as ContextGraphEdgeItem[],
    compatibilitySummary,
  };
  const governedNodeIds=new Set(canonicalBehavior.contextGraph?.recentNodes.map(node=>node.id) || []);
  canonicalBehavior.contextGraphEdges=canonicalBehavior.contextGraphEdges?.filter(edge=>governedNodeIds.has(edge.from_node_id)&&governedNodeIds.has(edge.to_node_id));
  const builtLongitudinalAggregate = buildCoachVeeraBehavioralReadModel({
    biThemes: canonicalBehavior.biThemes,
    biClusters: canonicalBehavior.biClusters,
    contextGraph: canonicalBehavior.contextGraph,
    compatibilitySummary: canonicalBehavior.compatibilitySummary,
  });
  const longitudinalAggregate = contextGraphNodes.error || contextGraphEdges.error
    ? {
        ...builtLongitudinalAggregate,
        sourceSummary: {
          ...builtLongitudinalAggregate.sourceSummary,
          graphEvidenceCount: null,
        },
      }
    : builtLongitudinalAggregate;
  const selectedBehaviorKey = normalizeBehavioralKey(longitudinalAggregate.primaryObservation.behaviorKey);
  const aggregateEvidenceRefs: CoachLeaderAggregateEvidenceRef[] = canonicalBehavior.biObservations.flatMap((observation) => {
    const sourceType = canonicalizeCoachLeaderLongitudinalSourceType(observation.source_type);
    if (
      !sourceType
      || normalizeBehavioralKey(observation.behavior_theme) !== selectedBehaviorKey
      || !nonEmptyString(observation.source_artifact_id)
    ) {
      return [];
    }
    return [{
      behaviorKey: longitudinalAggregate.primaryObservation.behaviorKey,
      sourceType,
      sourceArtifactId: observation.source_artifact_id,
      meetingId: observation.meeting_id,
      evidenceType: observation.evidence_type,
      isContradiction: observation.metadata?.polarity === "contradicts",
    }];
  });
  const observationRows = (meetingBehaviorObservations.data || []) as MeetingBehaviorObservationRow[];
  const authorizedSources = behavioralObservations.error
    ? { status: "operational_error" as const, sources: [] as const }
    : projectAuthorizedLongitudinalSources({
      transcriptResponse: transcriptRows,
      reflectionResponse: postReflections,
      artifactResponse: artifacts,
      transcripts,
      reflections,
      artifacts: artifactsItems,
    });
  const longitudinalIntelligence: CoachSessionPrepLongitudinalIntelligence = {
    aggregate: longitudinalAggregate,
    aggregateEvidenceRefs,
    aggregateReadiness: deriveAggregateReadiness({
      themes: behavioralThemes,
      clusters: behavioralClusters,
      graphNodes: contextGraphNodes,
      graphEdges: contextGraphEdges,
      compatibility: ledgerEvents,
      compatibilitySummary,
      crosswalk: behavioralObservations,
      legacy: meetingBehaviorObservations,
    }),
    authorizedSources,
    legacyEvidencePresent: meetingBehaviorObservations.error
      ? null
      : observationRows.some((row) => !hasCompleteLineageBundle(row)),
    observationWindowTruncated: meetingBehaviorObservations.error
      ? null
      : observationRows.length >= 51,
  };
  const behavioralObservation = buildCoachSessionPrepBehavioralObservation({
    canonicalBehavior,
    agreementRecord: prepContinuity.agreementRecord,
    latestCoachGrowthRecord: prepContinuity.latestCoachGrowthRecord,
  });
  // Re-read the active native coach lens for this authorized prep request.
  let frameworkPack:DbFrameworkPack|null=null;
  let frameworkReadError:unknown=null;
  try {
    const lens=await supabase.from('coach_lens').select(edgeColumns('coach_lens',['coach_lens_json']))
      .eq('coach_id',coachId).eq('is_active',true).returns<Array<{coach_lens_json:import('@/integrations/supabase/types').Json}>>().maybeSingle();
    if(lens.error)throw lens.error;
    const lensJson=lens.data?.coach_lens_json;
    frameworkPack=await resolveLeaderFrameworkPack(supabase,{coachId,
      coachLensJson:lensJson && typeof lensJson==='object'&&!Array.isArray(lensJson)?lensJson:null,
      leadershipLens:onboarding.data?.leadership_lens,throwOnError:true},true);
  }catch(error){console.warn("prep_framework_context_unavailable");frameworkPack=null;frameworkReadError=error;}
  const practiceContext=await loadPracticeContext(supabase,leaderId);
  if(!frameworkReadError&&biContext.status!=='unavailable'&&withholdsUnboundBiInterpretations(biContext)){
    const packStale=await revalidateResolvedFrameworkPack(frameworkPack,()=>resolveCurrentFrameworkPack(supabase,{leaderId,coachId,coachSource:'actor'},true));
    if(packStale)throw new Error(packStale.error);
  }
  const stale=await revalidateGovernedBiContext(supabase as unknown as BiRpcClient,biContext);
  if(stale)throw new Error(stale.error);
  const sourceReadiness = buildCoachLeaderDetailSourceReadiness({
    required: {
      relationship: "ready",
      profile: "ready",
    },
    optional: {
      transcripts: optionalSourceState([transcriptRows]),
      reflections: optionalSourceState([postReflections, assessments, sharedReflections]),
      artifacts: optionalSourceState([artifacts]),
      practice: optionalSourceState([
        {data:practiceContext.items,error:practiceContext.status==='unavailable'?'practice_context_unavailable':null},
        practiceSummaries,
        practiceFocusSuggestions,
        practiceFocusEvidence,
        meetingFeedback,
      ]),
      graph: optionalSourceState([contextGraphNodes, contextGraphEdges]),
      intelligence: optionalSourceState([
        ledgerEvents,
        behavioralObservations,
        behavioralThemes,
        behavioralClusters,
        meetingBehaviorObservations,
      ]),
      coachContext: optionalSourceState([
        {data:frameworkPack,error:frameworkReadError},
        onboarding,
        coachProfile,
        activeCoachTwin,
        coachAgreementMemory,
        leaderMemoryInputs,
      ]),
      billing: "unknown",
      unreadMessages: "unknown",
      processingUsage: "unknown",
    },
  });

  return {
    contextSources,
    frameworkPack,
    frameworkContextStatus:frameworkReadError?'unavailable' as const:'ready' as const,
    practiceContext,
    profile,
    onboarding: onboarding.data,
    transcripts,
    reflections,
    artifacts: artifactsItems,
    practiceSummaries: safePracticeSummaries as PracticeSummaryItem[],
    practiceFocusSuggestions: practiceFocusSuggestionItems,
    meetingFeedback: safeMeetingFeedback as MeetingFeedbackItem[],
    ledgerEvents: normalizedLedgerEvents,
    leaderMemoryInputs: safeLeaderMemoryInputs,
    coachLens: {
      coaching_philosophy: coachProfile.data?.coaching_philosophy || null,
      frameworks: coachProfile.data?.frameworks || null,
      voice_summary: (activeCoachTwin.data?.twin_json as { voice_summary?: string } | null)?.voice_summary || null,
    },
    canonicalBehavior,
    agreementRecord: prepContinuity.agreementRecord,
    continuity: prepContinuity,
    latestCoachGrowthRecord: prepContinuity.latestCoachGrowthRecord,
    behavioralObservation,
    longitudinalIntelligence,
    interventionMemory: prepContinuity.interventionMemory,
    sourceReadiness,
  };
}

export function useCoachSessionPrepData(params: {
  coachId?: string;
  leaderId?: string;
  accessGeneration?: string;
  enabled?: boolean;
}) {
  const { coachId, leaderId, accessGeneration, enabled = true } = params;

  const query = useQuery({
    queryKey: [
      ...appQueryKeys.coachSessionPrepV2(coachId, leaderId),
      accessGeneration ?? "no-access-generation",
    ],
    enabled: Boolean(enabled && coachId && leaderId),
    staleTime:0,
    refetchOnMount:"always",
    queryFn: async () => fetchCoachSessionPrepData({
      coachId: coachId!,
      leaderId: leaderId!,
    }),
  });
  return {...query,data:query.isFetching || query.isError ? undefined : query.data,isLoading:query.isLoading || query.isFetching};
}

/** Browser-local diagnostics, emitted after the selected brief is committed to the view. */
export function useCoachSessionPrepReceipt(params: {
  enabled: boolean;
  assemblyKey: string;
  sources?: readonly ContextSourceReceipt[];
  selection: ContextSelectionManifest;
  queryFailed?: boolean;
}) {
  const lastAssembly = useRef<{ key: string; selection: ContextSelectionManifest } | null>(null);
  useEffect(() => {
    if (!params.enabled) return;
    try {
      // The private key is used only for local deduplication; never emitted.
      const sources: readonly ContextSourceReceipt[] = params.queryFailed
        ? [{ kind: "session_prep_fetch", status: "unavailable", row_count: 0, reason_code: "query_failed" }]
        : params.sources ?? [];
      const selection: ContextSelectionManifest = params.queryFailed
        ? { final_evidence: [], truncations: [], unattributed_selections: [] }
        : params.selection;
      const key = JSON.stringify([params.assemblyKey, sources, selection]);
      if (lastAssembly.current?.key === key
        && (params.queryFailed || lastAssembly.current.selection === params.selection)) return;
      lastAssembly.current = { key, selection: params.selection };
      createContextAssemblyReceiptEmitter()({
        surface: "session_prep", path: "full",
        sources, selection,
      });
    } catch {
      try { console.warn("[context-receipt] receipt_invalid"); } catch { /* Diagnostics cannot fail the view. */ }
    }
  }, [params.enabled, params.assemblyKey, params.sources, params.selection, params.queryFailed]);
}
